// CombatPreviewPanel — U5.
//
// Renders the predicted outcome of the selected unit attacking each
// adjacent enemy unit. Driven entirely by the pure-TS predictor, so the
// displayed numbers always match the sim's canonical matrices. Mounted
// next to the UnitSelectionTray or inside the InfoPanel for owned units.

import type { GameSnapshot, SceneNode } from '@aod/protocol';
import { type MessageKey, useT } from '../../i18n/index.js';
import {
  type CombatPrediction,
  predictCombatOutcome,
} from '../../sim/useSimWorld/combatPredictor.js';
import { findAdjacentEnemyUnits } from './adjacentEnemies.js';

export interface CombatPreviewPanelProps {
  readonly attacker: SceneNode;
  readonly snapshot: GameSnapshot;
  readonly localPlayerIndex: number;
}

const OUTCOME_STYLES: Record<CombatPrediction['outcome'], { label: MessageKey; cls: string }> = {
  favorable: {
    label: 'preview.favorable',
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  },
  even: { label: 'preview.even', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  unfavorable: {
    label: 'preview.unfavorable',
    cls: 'bg-red-500/15 text-red-700 dark:text-red-300',
  },
};

function fmtMultiplier(n: number): string {
  return `${n.toFixed(2)}×`;
}

export function CombatPreviewPanel({
  attacker,
  snapshot,
  localPlayerIndex,
}: CombatPreviewPanelProps) {
  const t = useT();
  const enemies = findAdjacentEnemyUnits({ attacker, snapshot, localPlayerIndex });
  if (enemies.length === 0) return null;

  const attackerKind = (attacker.props?.kind as string | undefined) ?? 'unit';
  const attackerFormation =
    (attacker.props?.formation as string | undefined as Parameters<
      typeof predictCombatOutcome
    >[0]['attackerFormation']) ?? 'Standard';
  const attackerHp = (attacker.props?.hp as number | undefined) ?? 0;
  const attackerHpMax = (attacker.props?.hpMax as number | undefined) ?? 1;

  const allNodes: ReadonlyArray<SceneNode> = snapshot.scene.children ?? [];

  return (
    <section
      data-testid="combat-preview-panel"
      className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-card/50 p-2"
    >
      <header className="flex items-center justify-between">
        <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
          {t('preview.title')}
        </span>
        <span className="text-[10px] text-muted-foreground/80">
          {t('preview.vsNearby', { kind: attackerKind })}
        </span>
      </header>
      <ul className="flex flex-col gap-1">
        {enemies.map((enemy) => {
          const dq = enemy.props?.q as number | undefined;
          const dr = enemy.props?.r as number | undefined;
          const tile =
            dq !== undefined && dr !== undefined
              ? allNodes.find(
                  (n) =>
                    n.type === 'tile' &&
                    (n.props?.q as number | undefined) === dq &&
                    (n.props?.r as number | undefined) === dr,
                )
              : undefined;
          const defenderTerrain = (tile?.props?.biome as string | undefined) ?? 'plains';
          const prediction = predictCombatOutcome({
            attackerKind,
            attackerFormation,
            defenderKind: (enemy.props?.kind as string | undefined) ?? 'warrior',
            defenderFormation:
              (enemy.props?.formation as string | undefined as Parameters<
                typeof predictCombatOutcome
              >[0]['defenderFormation']) ?? 'Standard',
            defenderTerrain,
            attackerHp,
            attackerHpMax,
            defenderHp: (enemy.props?.hp as number | undefined) ?? 0,
            defenderHpMax: (enemy.props?.hpMax as number | undefined) ?? 1,
          });
          const style = OUTCOME_STYLES[prediction.outcome];
          const enemyKind = (enemy.props?.kind as string | undefined) ?? 'unit';
          return (
            <li
              key={enemy.id}
              data-testid="combat-preview-row"
              className="flex items-center justify-between gap-2 rounded bg-muted/30 px-2 py-1"
            >
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="font-medium text-foreground">{enemyKind}</span>
                {prediction.terrainBonus > 0 && (
                  <span
                    className="rounded bg-stone-500/15 px-1 text-[9px] font-medium text-stone-700 dark:text-stone-300"
                    title={t('preview.terrainTitle', { bonus: prediction.terrainBonus })}
                  >
                    {t('preview.terrain', { bonus: prediction.terrainBonus })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] tabular-nums text-muted-foreground">
                {prediction.classMultiplier !== null && (
                  <span title={t('preview.classCounter')}>
                    c {fmtMultiplier(prediction.classMultiplier)}
                  </span>
                )}
                <span title={t('preview.formationCounter')}>
                  f {fmtMultiplier(prediction.formationMultiplier)}
                </span>
                <span className="font-semibold text-foreground" title={t('preview.total')}>
                  {fmtMultiplier(prediction.totalMultiplier)}
                </span>
                <span
                  data-testid="combat-preview-outcome"
                  className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${style.cls}`}
                >
                  {t(style.label)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
